import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getBookings, 
  getUsers, 
  getAdminSettings, 
  updateAdminSettings,
  assignConsultantToBooking,
  createTestSurvey,
  fixConsultantIds,
  backfillMissingSurveys
} from '../lib/api';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FlaskConical, 
  UserCheck, 
  FileText, 
  Loader2, 
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Search,
  Copy,
  Wrench,
  Users,
  Mail
} from 'lucide-react';

const AdminTestPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  // Survey dialog
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [surveyResult, setSurveyResult] = useState(null);
  const [creatingSurvey, setCreatingSurvey] = useState(false);
  
  // Success message
  const [successMessage, setSuccessMessage] = useState('');
  
  // Maintenance operations
  const [fixingConsultants, setFixingConsultants] = useState(false);
  const [backfillingSurveys, setBackfillingSurveys] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes, bookingsRes] = await Promise.all([
        getAdminSettings(),
        getUsers({ role: 'consultant' }),
        getBookings()
      ]);
      setSettings(settingsRes.data);
      setConsultants(usersRes.data.filter(u => u.role === 'consultant' || u.role === 'admin'));
      setBookings(bookingsRes.data.slice(0, 100)); // Limit to 100 for performance
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoEmails = async (enabled) => {
    try {
      await updateAdminSettings(enabled);
      setSettings({ ...settings, auto_send_survey_emails: enabled });
      setSuccessMessage(enabled ? 'Auto-emails aktiveret' : 'Auto-emails deaktiveret (test-tilstand)');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleAssignConsultant = async () => {
    if (!selectedBooking || !selectedConsultant) return;
    
    setAssigning(true);
    try {
      await assignConsultantToBooking(selectedBooking.hubhus_booking_id, selectedConsultant);
      setAssignDialogOpen(false);
      setSelectedBooking(null);
      setSelectedConsultant('');
      setSuccessMessage('Konsulent tildelt');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadData(); // Refresh bookings
    } catch (error) {
      console.error('Failed to assign consultant:', error);
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateTestSurvey = async (sendEmail = false) => {
    if (!selectedBooking) return;
    
    setCreatingSurvey(true);
    try {
      const response = await createTestSurvey(selectedBooking.hubhus_booking_id, sendEmail);
      setSurveyResult(response.data);
    } catch (error) {
      console.error('Failed to create survey:', error);
    } finally {
      setCreatingSurvey(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Link kopieret');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  const handleFixConsultantIds = async () => {
    setFixingConsultants(true);
    setMaintenanceResult(null);
    try {
      const response = await fixConsultantIds();
      setMaintenanceResult({
        type: 'fix-consultants',
        success: true,
        data: response.data
      });
      setSuccessMessage(`${response.data.fixed} bookings repareret`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to fix consultant IDs:', error);
      setMaintenanceResult({
        type: 'fix-consultants',
        success: false,
        error: error.response?.data?.detail || 'Fejl ved reparation'
      });
    } finally {
      setFixingConsultants(false);
    }
  };

  const handleBackfillSurveys = async () => {
    setBackfillingSurveys(true);
    setMaintenanceResult(null);
    try {
      const response = await backfillMissingSurveys();
      setMaintenanceResult({
        type: 'backfill-surveys',
        success: true,
        data: response.data
      });
      setSuccessMessage(`${response.data.created} surveys oprettet`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to backfill surveys:', error);
      setMaintenanceResult({
        type: 'backfill-surveys',
        success: false,
        error: error.response?.data?.detail || 'Fejl ved backfill'
      });
    } finally {
      setBackfillingSurveys(false);
    }
  };

  const formatDateTime = (isoString) => {
    try {
      return format(parseISO(isoString), "d. MMM HH:mm", { locale: da });
    } catch (e) {
      return isoString;
    }
  };

  const filteredBookings = bookings.filter(b => 
    b.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.hubhus_booking_id.includes(searchQuery)
  );

  if (loading) {
    return (
      <div className="max-w-6xl">
        <Skeleton className="h-8 w-48 mb-8" />
        <Skeleton className="h-32 w-full mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 font-outfit flex items-center gap-3">
          <FlaskConical className="h-8 w-8 text-teal-600" />
          Test & Opsætning
        </h1>
        <p className="text-slate-500 mt-1">Test systemet uden at sende emails til kunder</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 mb-6">
          <CheckCircle2 className="h-5 w-5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Test Mode Settings */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-outfit">Test-tilstand</CardTitle>
          <CardDescription>
            Når test-tilstand er aktiv, sendes der IKKE automatisk survey-emails til kunder
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${settings?.auto_send_survey_emails ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                {settings?.auto_send_survey_emails ? <AlertCircle className="h-5 w-5" /> : <FlaskConical className="h-5 w-5" />}
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  {settings?.auto_send_survey_emails ? 'Produktion' : 'Test-tilstand aktiv'}
                </p>
                <p className="text-sm text-slate-500">
                  {settings?.auto_send_survey_emails 
                    ? 'Survey-emails sendes automatisk til nye bookinger' 
                    : 'Emails sendes kun manuelt'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="auto-emails" className="text-sm text-slate-600">Auto-emails</Label>
              <Switch
                id="auto-emails"
                checked={settings?.auto_send_survey_emails || false}
                onCheckedChange={handleToggleAutoEmails}
                data-testid="auto-emails-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Maintenance */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-outfit flex items-center gap-2">
            <Wrench className="h-5 w-5 text-slate-600" />
            System Vedligeholdelse
          </CardTitle>
          <CardDescription>
            Reparationsværktøjer til at sikre data-integritet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Fix Consultant IDs */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Reparer Konsulent-IDs</p>
                <p className="text-sm text-slate-500">
                  Matcher bookings med konsulenter baseret på email
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleFixConsultantIds}
              disabled={fixingConsultants}
              data-testid="fix-consultant-ids-btn"
            >
              {fixingConsultants ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Kør reparation
            </Button>
          </div>

          {/* Backfill Surveys */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Opret manglende Surveys</p>
                <p className="text-sm text-slate-500">
                  Opretter surveys for bookings der mangler dem (kun Group A)
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleBackfillSurveys}
              disabled={backfillingSurveys}
              data-testid="backfill-surveys-btn"
            >
              {backfillingSurveys ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Kør backfill
            </Button>
          </div>

          {/* Maintenance Result */}
          {maintenanceResult && (
            <div className={`p-4 rounded-lg ${maintenanceResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
              {maintenanceResult.success ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-700 font-medium">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>{maintenanceResult.data.message}</span>
                  </div>
                  {maintenanceResult.type === 'fix-consultants' && (
                    <div className="text-sm text-emerald-600 ml-7">
                      <p>Repareret: <strong>{maintenanceResult.data.fixed}</strong></p>
                      <p>Email ikke fundet: <strong>{maintenanceResult.data.email_not_found}</strong></p>
                    </div>
                  )}
                  {maintenanceResult.type === 'backfill-surveys' && (
                    <div className="text-sm text-emerald-600 ml-7">
                      <p>Oprettet: <strong>{maintenanceResult.data.created}</strong></p>
                      <p>Sprunget over (fortid): <strong>{maintenanceResult.data.skipped_past_appointments}</strong></p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span>{maintenanceResult.error}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookings for Testing */}
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-outfit">Bookinger til test</CardTitle>
              <CardDescription>Vælg en booking for at tildele konsulent og oprette test-survey</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Søg booking..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-bookings"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tidspunkt</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Konsulent</TableHead>
                <TableHead>Survey</TableHead>
                <TableHead className="text-right">Handlinger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.slice(0, 20).map((booking) => (
                <TableRow key={booking.id} data-testid={`test-booking-${booking.hubhus_booking_id}`}>
                  <TableCell className="font-mono text-sm">
                    {formatDateTime(booking.scheduled_start)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{booking.customer_name}</p>
                      <p className="text-xs text-slate-500">#{booking.hubhus_booking_id}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {booking.consultant_name ? (
                      <Badge className="bg-emerald-50 text-emerald-700">
                        {booking.consultant_name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">
                        Ikke tildelt
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      booking.survey_status === 'received'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-slate-100 text-slate-600'
                    }>
                      {booking.survey_status === 'received' ? 'Modtaget' : 'Afventer'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedBooking(booking);
                        setAssignDialogOpen(true);
                      }}
                      data-testid={`assign-btn-${booking.hubhus_booking_id}`}
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Tildel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedBooking(booking);
                        setSurveyResult(null);
                        setSurveyDialogOpen(true);
                      }}
                      data-testid={`survey-btn-${booking.hubhus_booking_id}`}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Survey
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredBookings.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              Ingen bookinger fundet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Consultant Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Tildel konsulent</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Booking</p>
                <p className="font-medium">{selectedBooking.customer_name}</p>
                <p className="text-sm text-slate-600">{formatDateTime(selectedBooking.scheduled_start)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Vælg konsulent</Label>
                <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                  <SelectTrigger data-testid="select-consultant">
                    <SelectValue placeholder="Vælg en konsulent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.disc_status === 'ok' ? `(${c.disc_profile_json?.primary || ''})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={handleAssignConsultant}
                disabled={!selectedConsultant || assigning}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="confirm-assign-btn"
              >
                {assigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Tildel konsulent
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Survey Dialog */}
      <Dialog open={surveyDialogOpen} onOpenChange={setSurveyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Test Survey</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Booking</p>
                <p className="font-medium">{selectedBooking.customer_name}</p>
                {selectedBooking.customer_email && (
                  <p className="text-sm text-slate-600">{selectedBooking.customer_email}</p>
                )}
              </div>
              
              {surveyResult ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>{surveyResult.message}</span>
                  </div>
                  
                  <div className="p-3 bg-teal-50 rounded-lg">
                    <p className="text-sm text-slate-600 mb-2">Survey link (til test):</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-white rounded text-sm break-all">
                        {surveyResult.survey_url}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(window.location.origin + surveyResult.survey_url)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(surveyResult.survey_url, '_blank')}
                    data-testid="open-survey-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Åbn survey i ny fane
                  </Button>
                  
                  {surveyResult.email_sent && (
                    <p className="text-sm text-emerald-600 text-center">
                      Email sendt til {surveyResult.customer_email}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={() => handleCreateTestSurvey(false)}
                    disabled={creatingSurvey}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    data-testid="create-survey-no-email"
                  >
                    {creatingSurvey && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Opret survey (uden email)
                  </Button>
                  
                  {selectedBooking.customer_email && (
                    <Button
                      variant="outline"
                      onClick={() => handleCreateTestSurvey(true)}
                      disabled={creatingSurvey}
                      className="w-full"
                      data-testid="create-survey-with-email"
                    >
                      Opret survey og send email
                    </Button>
                  )}
                  
                  <p className="text-xs text-slate-500 text-center">
                    Survey-linket kan bruges til at teste flowet manuelt
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestPage;
